import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { startupsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Rocket, Globe, MapPin, Users, Calendar, ExternalLink, Briefcase } from 'lucide-react';

export const StartupDetailPage = () => {
  const { id } = useParams();
  const [startup, setStartup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStartup = async () => {
      try {
        const res = await startupsAPI.getById(id);
        setStartup(res.data);
      } catch (err) {
        console.error('Error fetching startup:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStartup();
  }, [id]);

  if (loading) {
    return (
      <div className="container-wide py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-12 bg-muted rounded w-1/2" />
          <div className="h-4 bg-muted rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!startup) {
    return (
      <div className="container-wide py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Startup not found</h1>
        <Link to="/startups">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Startups
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="startup-detail-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <Link to="/startups" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Startups
          </Link>
          
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-sm bg-muted flex items-center justify-center overflow-hidden">
              {startup.logo_url ? (
                <img src={startup.logo_url} alt={startup.name} className="w-full h-full object-cover" />
              ) : (
                <Rocket className="w-10 h-10 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="badge-category">{startup.industry}</span>
                <span className="text-sm text-muted-foreground capitalize">{startup.stage}</span>
                {startup.is_hiring && (
                  <span className="text-xs bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-1 rounded-full">
                    Hiring
                  </span>
                )}
              </div>
              <h1 className="text-display text-3xl lg:text-4xl mb-3">{startup.name}</h1>
              <p className="text-muted-foreground max-w-2xl">{startup.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section>
        <div className="container-wide py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* About */}
              <div>
                <h2 className="text-heading text-xl mb-4">About {startup.name}</h2>
                <p className="text-muted-foreground leading-relaxed">{startup.description}</p>
              </div>

              {/* Tags */}
              {startup.tags && startup.tags.length > 0 && (
                <div>
                  <h2 className="text-heading text-xl mb-4">Technologies & Focus Areas</h2>
                  <div className="flex flex-wrap gap-2">
                    {startup.tags.map((tag, i) => (
                      <span key={i} className="badge-category">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6 space-y-4">
                  {startup.location && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{startup.location}</div>
                        <div className="text-xs text-muted-foreground">Location</div>
                      </div>
                    </div>
                  )}
                  {startup.founded_year && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{startup.founded_year}</div>
                        <div className="text-xs text-muted-foreground">Founded</div>
                      </div>
                    </div>
                  )}
                  {startup.employee_count && (
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{startup.employee_count}</div>
                        <div className="text-xs text-muted-foreground">Employees</div>
                      </div>
                    </div>
                  )}
                  {startup.website && (
                    <a
                      href={startup.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-primary hover:underline"
                    >
                      <Globe className="w-5 h-5" />
                      <span>Visit Website</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </CardContent>
              </Card>

              {startup.is_hiring && (
                <Link to="/jobs">
                  <Button className="w-full" size="lg">
                    <Briefcase className="w-4 h-4 mr-2" />
                    View Open Positions
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
